import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Search } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal, MaskLines } from "../components/Reveal";
import { FILMS, CONCEPTS, STATUSES } from "../data/films";

const FILTERS = [
  { key: "all", label: "All Projects" },
  { key: STATUSES.COMPLETED, label: "Completed" },
  { key: STATUSES.DEVELOPED, label: "Developed" },
  { key: STATUSES.IN_PRODUCTION, label: "In Production" },
  { key: STATUSES.ON_THE_FLOOR, label: "On the Floor" },
  { key: STATUSES.CONCEPT, label: "Concept" },
];

export default function Films() {
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");

  const all = [...FILMS, ...CONCEPTS];
  const filtered = all
    .filter((f) => filter === "all" || f.status === filter)
    .filter((f) =>
      !query ||
      f.title.toLowerCase().includes(query.toLowerCase()) ||
      f.genre.toLowerCase().includes(query.toLowerCase()) ||
      (f.nepaliTitle || "").includes(query) ||
      (f.hindiTitle || "").includes(query)
    );

  return (
    <main data-testid="films-page" className="bg-black pt-32 md:pt-40">
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-16">
        <p className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)] mb-6">Projects</p>
        <h1 className="font-serif-display font-light text-white text-6xl md:text-8xl lg:text-9xl leading-[0.9] tracking-tighter">
          <MaskLines lines={["The full", "slate."]} />
        </h1>
        <Reveal delay={1} className="mt-10 max-w-xl">
          <p className="text-white/60 leading-relaxed">
            Three completed short films & documentaries, seven developed features, three
            in production, two currently on the floor — plus two concept projects.
          </p>
        </Reveal>
      </section>

      {/* Filter + Search */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-10">
        <div className="flex flex-wrap gap-3 md:gap-4 items-center border-t border-white/10 pt-8">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              data-testid={`filter-${f.key.replace(/\s+/g, "-").toLowerCase()}`}
              className={`text-[10px] uppercase tracking-overline px-4 py-2 border transition-colors ${
                filter === f.key
                  ? "bg-[color:var(--dk-gold)] text-black border-[color:var(--dk-gold)]"
                  : "border-white/20 text-white/70 hover:border-white/60 hover:text-white"
              }`}
            >
              {f.label}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2 border-b border-white/20 focus-within:border-[color:var(--dk-gold)] transition-colors">
            <Search size={14} className="text-white/50" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search projects…"
              data-testid="films-search"
              className="bg-transparent text-sm text-white placeholder:text-white/40 py-2 px-1 focus:outline-none w-48"
            />
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-28">
        {filtered.length === 0 ? (
          <p className="text-white/50 py-24 text-center">No projects match your filter.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {filtered.map((f, i) => (
              <FilmCard key={f.slug} film={f} index={i} />
            ))}
          </div>
        )}
      </section>
    </main>
  );
}

function FilmCard({ film, index }) {
  const badge = statusColor(film.status);
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay: (index % 3) * 0.08, ease: [0.2, 0.8, 0.2, 1] }}
      data-testid={`films-row-${film.slug}`}
    >
      <Link to={`/films/${film.slug}`} className="group block border border-white/10 bg-[color:var(--dk-muted)] hover:border-[color:var(--dk-gold)]/50 transition-colors">
        <div className="relative aspect-[3/4] overflow-hidden">
          <img src={film.poster} alt={film.title} className="film-hover-img absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/30 to-transparent" />
          <span className={`absolute top-4 left-4 text-[10px] uppercase tracking-overline px-3 py-1 ${badge.bg} ${badge.text}`}>
            {film.status}
          </span>
          <ArrowUpRight size={18} className="absolute top-4 right-4 text-white/70 group-hover:text-[color:var(--dk-gold)] transition-colors" />
        </div>
        <div className="p-5 md:p-6">
          <p className="text-[10px] uppercase tracking-overline text-white/50 mb-2">{film.genre}</p>
          <h3 className="font-serif-display text-white text-2xl md:text-3xl leading-tight group-hover:text-[color:var(--dk-gold)] transition-colors">
            {film.title}
          </h3>
          {film.nepaliTitle && (
            <p className="mt-2 text-white/40 text-xs font-serif-display italic">
              {film.nepaliTitle} · {film.hindiTitle}
            </p>
          )}
          <p className="mt-4 text-white/60 text-sm leading-relaxed line-clamp-3">{film.shortSynopsis}</p>
        </div>
      </Link>
    </motion.div>
  );
}

function statusColor(status) {
  switch (status) {
    case STATUSES.COMPLETED: return { bg: "bg-white", text: "text-black" };
    case STATUSES.DEVELOPED: return { bg: "bg-[color:var(--dk-gold)]", text: "text-black" };
    case STATUSES.IN_PRODUCTION: return { bg: "bg-[color:var(--dk-red)]", text: "text-white" };
    case STATUSES.ON_THE_FLOOR: return { bg: "bg-[color:var(--dk-gold)]", text: "text-black" };
    default: return { bg: "bg-white/10 border border-white/30", text: "text-white/80" };
  }
}
